import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const CATEGORIES = ["Onboarding", "Playbooks", "Frameworks", "Templates"];

export default function KnowledgeFormDialog({ open, onOpenChange, resource, onCreated }) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Onboarding");
  const [description, setDescription] = useState("");
  const [link, setLink] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const isEdit = !!resource;

  useEffect(() => {
    if (open) {
      if (resource) {
        setTitle(resource.title);
        setCategory(resource.category);
        setDescription(resource.description || "");
        setLink(resource.link || "");
      } else {
        setTitle(""); setCategory("Onboarding"); setDescription(""); setLink("");
      }
      setFile(null);
    }
  }, [open, resource]);

  const handleSubmit = async () => {
    if (!title || !category) {
      toast.error("Title and category are required");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("category", category);
      fd.append("description", description || "");
      fd.append("link", link || "");
      if (file) fd.append("file", file);
      if (isEdit) {
        await api.put(`/knowledge/${resource.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Resource updated");
      } else {
        await api.post("/knowledge", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Resource added to Knowledge Hub");
      }
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="knowledge-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit Resource" : "Add Knowledge Resource"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input data-testid="knowledge-form-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Client Proposal Template" />
          </div>
          <div className="space-y-1.5">
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="knowledge-form-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea data-testid="knowledge-form-description-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>
          <div className="space-y-1.5">
            <Label>External Link (optional)</Label>
            <Input data-testid="knowledge-form-link-input" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://..." />
          </div>
          <div className="space-y-1.5">
            <Label>{isEdit && resource?.original_filename ? `Replace File (current: ${resource.original_filename})` : "Or Upload File"}</Label>
            <Input data-testid="knowledge-form-file-input" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="knowledge-form-cancel-button">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="knowledge-form-submit-button" className="bg-[#76C121] hover:bg-[#5C9819] text-white">
            {submitting ? "Saving..." : isEdit ? "Save Changes" : "Add Resource"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
